import { useCallback, useEffect, useRef } from 'react';
import { AdapterType } from '@k-ucap/shared';
import type { ConnectionProfile } from '@k-ucap/shared';
import { createWebSocketMessageAdapter } from '@/infrastructure/adapters/WebSocketMessageAdapter.js';
import { getGlobalVirtualMessageAdapter } from '@/infrastructure/adapters/VirtualMessageAdapter.js';
import type { MessageTransportAdapter } from '@/infrastructure/adapters/MessageTransportAdapter.js';
import { useIntegrationStore } from '@/stores/useIntegrationStore.js';

export interface ManagedAdapter {
  profileId: string;
  adapter: MessageTransportAdapter;
}

export const useConnectionManager = () => {
  const adaptersRef = useRef<Map<string, ManagedAdapter>>(new Map());
  const virtualAdapter = getGlobalVirtualMessageAdapter();
  const registerAdapter = useIntegrationStore((s) => s.registerAdapter);
  const updateAdapterStatus = useIntegrationStore((s) => s.updateAdapterStatus);

  const connect = useCallback(
    (profile: ConnectionProfile) => {
      if (adaptersRef.current.has(profile.id)) return;

      registerAdapter(profile.id, profile.name, profile.adapterType);

      if (profile.adapterType !== AdapterType.WEBSOCKET) {
        const reason =
          profile.adapterType === AdapterType.LOCAL_BRIDGE ||
          profile.adapterType === AdapterType.UDP ||
          profile.adapterType === AdapterType.UDP_MULTICAST ||
          profile.adapterType === AdapterType.TCP_CLIENT ||
          profile.adapterType === AdapterType.TCP_SERVER
            ? 'LOCAL_BRIDGE_REQUIRED: this adapter type requires the local integration bridge (apps/bridge)'
            : 'NOT_IMPLEMENTED: this adapter type is not yet supported in the browser';
        updateAdapterStatus(profile.id, 'error', reason);
        return;
      }

      const url = String(profile.configuration.url ?? 'ws://localhost:8080');
      const adapter = createWebSocketMessageAdapter({
        url,
        projectId: profile.projectId,
        name: profile.id,
        token: String(profile.configuration.token ?? ''),
        heartbeatMs: Number(profile.configuration.heartbeatMs ?? 30000),
        reconnectIntervalMs: Number(profile.reconnectPolicy.intervalMs ?? 3000),
        maxReconnects: Number(profile.reconnectPolicy.maxRetries ?? 10),
      });

      adapter.subscribe((message) => {
        virtualAdapter.publish(message).catch((err) => {
          console.error('[ConnectionManager] Failed to publish inbound message to virtual bus:', err);
        });
      });

      adaptersRef.current.set(profile.id, { profileId: profile.id, adapter });
      adapter.connect().catch((err) => {
        console.error('[ConnectionManager] WebSocket connect failed:', err);
        updateAdapterStatus(profile.id, 'error', err instanceof Error ? err.message : 'Connection failed');
      });
    },
    [virtualAdapter, registerAdapter, updateAdapterStatus]
  );

  const disconnect = useCallback(
    (profileId: string) => {
      const managed = adaptersRef.current.get(profileId);
      if (!managed) {
        updateAdapterStatus(profileId, 'disconnected');
        return;
      }
      managed.adapter
        .disconnect()
        .catch((err) => {
          console.error('[ConnectionManager] Disconnect failed:', err);
        })
        .finally(() => {
          adaptersRef.current.delete(profileId);
        });
    },
    [updateAdapterStatus]
  );

  const sendTestMessage = useCallback(async (profile: ConnectionProfile) => {
    const managed = adaptersRef.current.get(profile.id);
    if (!managed) return;
    const message = {
      id: `test-${Date.now()}`,
      messageType: 101,
      standard: 'EXTERNAL',
      name: 'Test Inertial States',
      source: 'AIR_VEHICLE',
      target: 'CUCS',
      direction: 'TX',
      timestamp: new Date().toISOString(),
      fields: {
        vehicleId: 'UAV-TEST',
        timeStamp: Date.now(),
        latitude: 37.389,
        longitude: 126.647,
        altitudeM: 1200,
        northVelocityMps: 0,
        eastVelocityMps: 55,
        verticalVelocityMps: 0,
        rollDeg: 0,
        pitchDeg: 0,
        yawDeg: 90,
      },
      projectId: profile.projectId,
    };
    await managed.adapter.publish(message as unknown as import('@k-ucap/shared').MessageInstance);
  }, []);

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const currentAdapters = adaptersRef.current;
      currentAdapters.forEach((managed) => {
        managed.adapter.disconnect().catch((err) => {
          console.error('[ConnectionManager] Cleanup disconnect failed:', err);
        });
      });
      currentAdapters.clear();
    };
  }, []);

  return {
    connect,
    disconnect,
    sendTestMessage,
  };
};

export default useConnectionManager;
